import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '../../../core/app.service';
import { IconComponent } from '../../../shared/icon/icon.component';
import { GameScoresService } from '../../../core/game-scores.service';

/**
 * Number Match — a light numbers puzzle that lives under SYSTEM (a break between
 * bookings). Clear two cells when their digits are EQUAL or SUM TO 10 and a clear
 * straight line (row / column / diagonal, cleared cells don't block) connects them.
 * Empty rows drop; combos + board-clears score bonuses. Best score persists locally.
 * Ported from the OTA-B2B design (screens-settings.jsx · NumberMatch).
 */
const NM_COLS = 9;
const NM_KEY = 'vyg_nm_v1';
type Cell = number | null;

const nmRand = () => 1 + Math.floor(Math.random() * 9);
const nmSeed = (): Cell[] => Array.from({ length: 27 }, nmRand);

function nmCompact(cells: Cell[]): { cells: Cell[]; removed: number } {
  const rows: Cell[][] = [];
  for (let i = 0; i < cells.length; i += NM_COLS) rows.push(cells.slice(i, i + NM_COLS));
  const kept = rows.filter((r) => r.some((v) => v !== null));
  return { cells: kept.flat(), removed: rows.length - kept.length };
}
const nmValueOK = (a: number, b: number) => a === b || a + b === 10;
function nmBetweenEmpty(cells: Cell[], i: number, j: number, step: number): boolean {
  for (let k = i + step; k < j; k += step) if (cells[k] !== null) return false;
  return true;
}
function nmConnected(cells: Cell[], i: number, j: number): 'line' | 'col' | 'diag' | null {
  if (i > j) { const t = i; i = j; j = t; }
  const c1 = i % NM_COLS, r1 = (i / NM_COLS) | 0;
  const c2 = j % NM_COLS, r2 = (j / NM_COLS) | 0;
  if (nmBetweenEmpty(cells, i, j, 1)) return 'line';               // reading order / horizontal / wrap
  if (c1 === c2 && nmBetweenEmpty(cells, i, j, NM_COLS)) return 'col';
  if (Math.abs(r2 - r1) === Math.abs(c2 - c1)) {                    // diagonal
    const step = NM_COLS + (c2 > c1 ? 1 : -1);
    if (nmBetweenEmpty(cells, i, j, step)) return 'diag';
  }
  return null;
}
const nmCanMatch = (cells: Cell[], i: number, j: number) =>
  i !== j && cells[i] !== null && cells[j] !== null
  && nmValueOK(cells[i] as number, cells[j] as number) && !!nmConnected(cells, i, j);
function nmFindHint(cells: Cell[]): [number, number] | null {
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] === null) continue;
    for (let j = i + 1; j < cells.length; j++) {
      if (cells[j] !== null && nmCanMatch(cells, i, j)) return [i, j];
    }
  }
  return null;
}
function nmLoad(): { cells?: Cell[]; score?: number; best?: number; combo?: number } {
  try { return JSON.parse(localStorage.getItem(NM_KEY) || '{}') || {}; } catch { return {}; }
}

@Component({
  selector: 'app-number-match',
  standalone: true,
  imports: [IconComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './number-match.component.html',
})
export class NumberMatchComponent {
  private readonly app = inject(AppService);
  private readonly gameScores = inject(GameScoresService);
  /** Compact mode for the Toolbox Games gallery (floating window) — hides the
   *  page header + how-to-play rules; the board + slim stats fill the panel. */
  readonly embedded = input(false);
  readonly cols = NM_COLS;
  readonly digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  private readonly saved = nmLoad();
  readonly cells = signal<Cell[]>(this.saved.cells?.length ? this.saved.cells : nmSeed());
  readonly score = signal(this.saved.score ?? 0);
  readonly best = signal(this.saved.best ?? 0);
  readonly combo = signal(this.saved.combo ?? 0);
  readonly sel = signal<number | null>(null);
  readonly clearing = signal<{ a: number; b: number } | null>(null);
  readonly shake = signal<number | null>(null);
  readonly hint = signal<[number, number] | null>(null);
  readonly flash = signal<{ pts: number; id: number } | null>(null);
  readonly remote = this.gameScores.summary('number-match');
  readonly agencyBest = computed(() => this.remote().agencyBest);
  readonly agencyBestOwner = computed(() => this.remote().agencyBestOwnerName);
  readonly agencyBestLabel = computed(() => {
    const score = this.agencyBest();
    return score == null ? '—' : this.fmt(score);
  });

  readonly remaining = computed(() => this.cells().filter((v) => v !== null).length);
  readonly won = computed(() => this.remaining() === 0);
  readonly stuck = computed(() => !this.won() && !nmFindHint(this.cells()));

  constructor() {
    // Persist the whole game on any change (best score included).
    effect(() => {
      const data = { cells: this.cells(), score: this.score(), best: this.best(), combo: this.combo() };
      try { localStorage.setItem(NM_KEY, JSON.stringify(data)); } catch { /* storage unavailable */ }
    });

    effect(() => {
      const mine = this.remote().myBest;
      if (typeof mine === 'number' && mine > this.best()) this.best.set(mine);
    });

    effect(() => {
      const mine = this.remote().myBest ?? 0;
      const local = this.best();
      if (local > mine) this.gameScores.submitIfHigher('number-match', local);
    });
  }

  fmt(n: number): string { return n.toLocaleString('en-US'); }

  cellClass(idx: number, v: Cell): string {
    let c = 'nm-cell' + (v === null ? ' empty' : ' filled');
    if (idx === this.sel()) c += ' sel';
    if (idx === this.shake()) c += ' shake';
    const h = this.hint();
    if (h && (h[0] === idx || h[1] === idx)) c += ' hint';
    const cl = this.clearing();
    if (cl && (cl.a === idx || cl.b === idx)) c += ' clearing';
    return c;
  }

  reset(): void {
    this.cells.set(nmSeed()); this.score.set(0); this.combo.set(0);
    this.sel.set(null); this.hint.set(null); this.clearing.set(null);
  }

  addNumbers(): void {
    const rest = this.cells().filter((v) => v !== null);
    if (!rest.length) return;
    this.cells.update((c) => [...c, ...c.filter((v) => v !== null)]);
    this.combo.set(0); this.sel.set(null); this.hint.set(null);
    this.app.toast(`Added ${rest.length} numbers`);
  }

  showHint(): void {
    const h = nmFindHint(this.cells());
    if (h) {
      this.hint.set(h); this.sel.set(null);
      setTimeout(() => this.hint.set(null), 1400);
    } else {
      this.app.toast('No moves — add numbers');
    }
  }

  click(idx: number): void {
    const cells = this.cells();
    if (cells[idx] === null || this.clearing()) return;
    this.hint.set(null);
    const s = this.sel();
    if (s === null) { this.sel.set(idx); return; }
    if (s === idx) { this.sel.set(null); return; }
    if (nmCanMatch(cells, s, idx)) this.doMatch(s, idx);
    else { this.sel.set(idx); this.shake.set(idx); setTimeout(() => this.shake.set(null), 260); }
  }

  private doMatch(a: number, b: number): void {
    const cells = this.cells();
    const pairCombo = this.combo() + 1;
    const sumType = (cells[a] as number) + (cells[b] as number) === 10;
    this.sel.set(null);
    this.clearing.set({ a, b });
    setTimeout(() => {
      const next = this.cells().slice();
      next[a] = null; next[b] = null;
      const { cells: cc, removed } = nmCompact(next);
      let pts = 10 + (sumType ? 5 : 0) + (pairCombo - 1) * 2 + removed * 40;
      const cleared = cc.every((v) => v === null);
      if (cleared) pts += 200;
      const newScore = this.score() + pts;
      this.score.set(newScore);
      if (newScore > this.best()) {
        this.best.set(newScore);
        this.gameScores.submitIfHigher('number-match', newScore);
      }
      this.combo.set(pairCombo);
      this.cells.set(cc);
      this.clearing.set(null);
      this.flash.set({ pts, id: Date.now() });
      setTimeout(() => this.flash.set(null), 700);
      if (cleared) this.app.toast('Board cleared! +200 bonus');
    }, 240);
  }
}
